import { Injectable } from "@angular/core";
import { ComponentStore } from "@ngrx/component-store";
import { Observable } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { NewsletterTemplateDetailState } from "./newsletter-template-detail.state";
import { NewsletterTemplateModel } from "src/app/models/newsletter-template-model";
import { NewsletterTemplateService } from "src/app/services/newsletter-template.service";

const initialState: NewsletterTemplateDetailState = {
    loading: false,
    loaded: false,
    success: false,
    errorMessage: null,
    newsletterTemplate: null,
};

@Injectable({
    providedIn: 'root'
})
export class NewsletterTemplateDetailComponentStore extends ComponentStore<NewsletterTemplateDetailState> {

    constructor(private readonly newsletterTemplateService: NewsletterTemplateService) {
        super(initialState);
    }

    readonly newsletterTemplate$: Observable<NewsletterTemplateModel> = this.select(state => state.newsletterTemplate);
    readonly loading$: Observable<boolean> = this.select(state => state.loading);
    readonly loaded$: Observable<boolean> = this.select(state => state.loaded);
    readonly success$: Observable<boolean> = this.select(state => state.success);
    readonly errorMessage$: Observable<any> = this.select(state => state.errorMessage);

    readonly viewModel$ = this.select(
        this.newsletterTemplate$,
        this.loaded$,
        this.loading$,
        this.success$,
        this.errorMessage$,
        (newsletterTemplate, loaded, loading, success, errorMessage) => ({
            newsletterTemplate, loaded, loading, success, errorMessage
        })
    );

    readonly setInitial = this.updater((_: NewsletterTemplateDetailState) => {
        return {
            ...initialState
        };
    });

    readonly setLoading = this.updater((state: NewsletterTemplateDetailState) => {
        return {
            ...state,
            loading: true,
            loaded: false,
        };
    });

    readonly setLoaded = this.updater((state: NewsletterTemplateDetailState) => {
        return {
            ...state,
            loading: false,
            loaded: true,
        };
    });

    readonly updateNewsletterTemplateState = this.updater((state: NewsletterTemplateDetailState, value: {
        newsletterTemplate: NewsletterTemplateModel
    }) => {
        return {
            ...state,
            loading: false,
            loaded: true,
            success: true,
            newsletterTemplate: value.newsletterTemplate
        };
    });

    readonly setError = this.updater((state: NewsletterTemplateDetailState, value: {
        errorMessage: String
    }) => {
        return {
            ...state,
            loading: false,
            loaded: true,
            success: false,
            errorMessage: value.errorMessage,
        };
    });

    readonly getNewsletterTemplate = this.effect((params$: Observable<{ companyId: string, newsletterTemplateId: string }>) => {
        this.setLoading();
        return params$.pipe(
            switchMap((params => this.newsletterTemplateService.getNewsletterTemplate(params.companyId, params.newsletterTemplateId).pipe(
                map((newsletterTemplate) => {
                    this.updateNewsletterTemplateState({ newsletterTemplate: newsletterTemplate });
                    this.setLoaded();
                }),
                catchError(async (error) => {
                    this.setError({ errorMessage: error });
                    this.setLoaded();
                    this.setInitial();
                })
            ))
            )
        );
    });
}